import { Box, ToggleButton, ToggleButtonGroup, Typography, Button, IconButton } from '@mui/material';
import { 
  HorizontalRule as LineIcon, 
  CropSquare as RectangleIcon, 
  TextFields as TextIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  NearMe as SelectIcon,
  Create as DrawIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Star as IconShapeIcon
} from '@mui/icons-material';
import { useChartData } from './ChartDataProvider';
import { iconOptions, renderIconSvg } from '../../../Chart/iconsSvg';

const ShapesPanel = () => {
  const { 
    currentShape,
    selectedShape,
    shapeSelectMode,
    shapeStyle,
    actions 
  } = useChartData();

  // Available shape tools
  const shapeTools = [
    { key: 'line', label: 'Line', icon: <LineIcon /> },
    { key: 'rectangle', label: 'Rectangle', icon: <RectangleIcon /> },
    { key: 'text', label: 'Text', icon: <TextIcon /> },
    { key: 'icon', label: 'Icon', icon: <IconShapeIcon /> },
  ];

  const handleShapeSelect = (event, newShape) => {
    if (newShape !== null) {
      actions.setCurrentShape(newShape);
    }
  };

  const handleStyleChange = (property, value) => {
    actions.updateShapeStyle({
      ...shapeStyle,
      [property]: value
    });
  };

  const handleDeleteShape = () => {
    if (selectedShape) {
      actions.removeShape(selectedShape.id);
      actions.setSelectedShape(null);
    }
  };

  const handleEditShapeText = () => {
    if (selectedShape && selectedShape.type === 'text') {
      const newText = prompt('Edit text:', selectedShape.text);
      if (newText !== null) {
        actions.updateShape(selectedShape.id, { text: newText });
      }
    }
  };

  const handleShapeStyleChange = (property, value) => {
    if (selectedShape) {
      actions.updateShape(selectedShape.id, { [property]: value });
    } else {
      actions.updateShapeStyle({
        ...shapeStyle,
        [property]: value
      });
    }
  };

  // Rotation helper functions
  const handleRotateLeft = () => {
    if (selectedShape) {
      const currentRotation = selectedShape.rotation || 0;
      const newRotation = (currentRotation - 90 + 360) % 360;
      actions.updateShape(selectedShape.id, { rotation: newRotation });
    }
  };

  const handleRotateRight = () => {
    if (selectedShape) {
      const currentRotation = selectedShape.rotation || 0;
      const newRotation = (currentRotation + 90) % 360;
      actions.updateShape(selectedShape.id, { rotation: newRotation });
    }
  };

  const handleModeToggle = (event, newMode) => {
    if (newMode !== null) {
      const isSelectMode = newMode === 'select';
      actions.setShapeSelectMode(isSelectMode);
      
      // Clear current shape tool when switching to select mode
      if (isSelectMode) {
        actions.setCurrentShape(null);
        // Don't clear selected shape - keep it selected
      } else {
        // Clear selected shape when switching to draw mode
        actions.setSelectedShape(null);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Mode Toggle - Always visible */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Mode
        </Typography>
        <ToggleButtonGroup
          value={shapeSelectMode ? 'select' : 'draw'}
          exclusive
          onChange={handleModeToggle}
          size="small"
          sx={{ width: '100%' }}
        >
          <ToggleButton 
            value="select"
            sx={{ 
              flex: 1,
              justifyContent: 'center', 
              gap: 1,
              textTransform: 'none'
            }}
          >
            <SelectIcon />
            Select
          </ToggleButton>
          <ToggleButton 
            value="draw"
            sx={{ 
              flex: 1,
              justifyContent: 'center', 
              gap: 1,
              textTransform: 'none'
            }}
          >
            <DrawIcon />
            Draw
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Shape selection mode - selected shape editing */}
      {shapeSelectMode && selectedShape && (
        <>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Editing {selectedShape.type}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedShape.type === 'text' && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEditShapeText}
                variant="outlined"
              >
                Edit Text
              </Button>
            )}
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteShape}
              variant="outlined"
              color="error"
            >
              Delete
            </Button>
          </Box>

          {/* Shape Properties for selected shape */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Shape Properties
            </Typography>
            
            {/* Color Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['#000000', '#ff0000', '#8b0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#808080'].map((color) => (
                  <Box
                    key={color}
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: color,
                      border: selectedShape?.color === color ? '2px solid #333' : '1px solid #ccc',
                      borderRadius: 1,
                      cursor: 'pointer'
                    }}
                    onClick={() => handleShapeStyleChange('color', color)}
                  />
                ))}
              </Box>
            </Box>

            {/* Line/Rectangle specific properties */}
            {(selectedShape.type === 'line' || selectedShape.type === 'rectangle') && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Thickness: {selectedShape?.thickness || 1}px
                  </Typography>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={selectedShape?.thickness || 1}
                    onChange={(e) => handleShapeStyleChange('thickness', parseInt(e.target.value))}
                    style={{ width: '100%' }}
                  />
                </Box>
                
                {selectedShape.type === 'rectangle' && (
                  <Box sx={{ mb: 2 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedShape?.filled || false}
                        onChange={(e) => handleShapeStyleChange('filled', e.target.checked)}
                        style={{ marginRight: 8 }}
                      />
                      Filled
                    </label>
                  </Box>
                )}
              </>
            )}
            
            {/* Text specific properties */}
            {selectedShape.type === 'text' && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Font Size</Typography>
                  <select
                    value={selectedShape?.fontSize || 12}
                    onChange={(e) => handleShapeStyleChange('fontSize', parseInt(e.target.value))}
                    style={{ width: '100%', padding: 4 }}
                  >
                    <option value="10">10px</option>
                    <option value="12">12px</option>
                    <option value="14">14px</option>
                    <option value="16">16px</option>
                    <option value="18">18px</option>
                    <option value="20">20px</option>
                    <option value="24">24px</option>
                  </select>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Font Family</Typography>
                  <select
                    value={selectedShape?.fontFamily || 'Arial'}
                    onChange={(e) => handleShapeStyleChange('fontFamily', e.target.value)}
                    style={{ width: '100%', padding: 4 }}
                  >
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Verdana">Verdana</option>
                  </select>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedShape?.bold || false}
                      onChange={(e) => handleShapeStyleChange('bold', e.target.checked)}
                      style={{ marginRight: 8 }}
                    />
                    Bold
                  </label>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Rotation (degrees)</Typography>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="15"
                    value={selectedShape?.rotation || 0}
                    onChange={(e) => handleShapeStyleChange('rotation', parseInt(e.target.value))}
                    style={{ width: '100%' }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {selectedShape?.rotation || 0}°
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <IconButton size="small" onClick={handleRotateLeft} title="Rotate left 90°">
                      <RotateLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleRotateRight} title="Rotate right 90°">
                      <RotateRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Width: {selectedShape?.width || 200}px
                  </Typography>
                  <input
                    type="range"
                    min="50"
                    max="500"
                    step="10"
                    value={selectedShape?.width || 200}
                    onChange={(e) => handleShapeStyleChange('width', parseInt(e.target.value))}
                    style={{ width: '100%' }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Height: {selectedShape?.height || 24}px
                  </Typography>
                  <input
                    type="range"
                    min="20"
                    max="200"
                    step="5"
                    value={selectedShape?.height || 24}
                    onChange={(e) => handleShapeStyleChange('height', parseInt(e.target.value))}
                    style={{ width: '100%' }}
                  />
                </Box>
              </>
            )}

            {/* Icon specific properties */}
            {selectedShape.type === 'icon' && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Icon Type</Typography>
                  <select
                    value={selectedShape?.iconType || 'arrow'}
                    onChange={(e) => handleShapeStyleChange('iconType', e.target.value)}
                    style={{ width: '100%', padding: 4 }}
                  >
                    {iconOptions.filter(opt => opt.value).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Size: {selectedShape?.size || 16}px
                  </Typography>
                  <input
                    type="range"
                    min="12"
                    max="60"
                    step="2"
                    value={selectedShape?.size || 16}
                    onChange={(e) => handleShapeStyleChange('size', parseInt(e.target.value))}
                    style={{ width: '100%' }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedShape?.filled || false}
                      onChange={(e) => handleShapeStyleChange('filled', e.target.checked)}
                      style={{ marginRight: 8 }}
                    />
                    Filled
                  </label>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Rotation (degrees)</Typography>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="15"
                    value={selectedShape?.rotation || 0}
                    onChange={(e) => handleShapeStyleChange('rotation', parseInt(e.target.value))}
                    style={{ width: '100%' }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {selectedShape?.rotation || 0}°
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <IconButton size="small" onClick={handleRotateLeft} title="Rotate left 90°">
                      <RotateLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleRotateRight} title="Rotate right 90°">
                      <RotateRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </>
      )}

      {/* Shape selection mode - no shape selected */}
      {shapeSelectMode && !selectedShape && (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
          Click on a shape to select and edit it
        </Typography>
      )}

      {/* Shape drawing mode */}
      {!shapeSelectMode && (
        <>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Select a shape tool to draw on the chart
          </Typography>

          {/* Shape Tool Selection */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Shape Tools
            </Typography>
            <ToggleButtonGroup
              value={currentShape}
              exclusive
              onChange={handleShapeSelect}
              orientation="vertical"
              size="small"
              sx={{ width: '100%' }}
            >
              {shapeTools.map((tool) => (
                <ToggleButton 
                  key={tool.key} 
                  value={tool.key}
                  sx={{ 
                    justifyContent: 'flex-start', 
                    gap: 1,
                    textTransform: 'none'
                  }}
                >
                  {tool.icon}
                  {tool.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Shape Properties */}
          {currentShape && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {shapeTools.find(t => t.key === currentShape)?.label} Properties
              </Typography>
              
              {/* Color Selection */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['#000000', '#ff0000', '#8b0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#808080'].map((color) => (
                    <Box
                      key={color}
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: color,
                        border: shapeStyle?.color === color ? '2px solid #333' : '1px solid #ccc',
                        borderRadius: 1,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleStyleChange('color', color)}
                    />
                  ))}
                </Box>
              </Box>

              {/* Line/Rectangle specific properties */}
              {(currentShape === 'line' || currentShape === 'rectangle') && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Thickness: {shapeStyle?.thickness || 1}px
                    </Typography>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={shapeStyle?.thickness || 1}
                      onChange={(e) => handleStyleChange('thickness', parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  
                  {currentShape === 'rectangle' && (
                    <Box sx={{ mb: 2 }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={shapeStyle?.filled || false}
                          onChange={(e) => handleStyleChange('filled', e.target.checked)}
                          style={{ marginRight: 8 }}
                        />
                        Filled
                      </label>
                    </Box>
                  )}
                  
                  <Typography variant="body2" color="text.secondary">
                    {currentShape === 'line' ? 'Click and drag to draw a line. Snaps to grid.' : 'Click and drag to draw a rectangle. Snaps to grid.'}
                  </Typography>
                </>
              )}
              
              {/* Text specific properties */}
              {currentShape === 'text' && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Font Size</Typography>
                    <select
                      value={shapeStyle?.fontSize || 12}
                      onChange={(e) => handleStyleChange('fontSize', parseInt(e.target.value))}
                      style={{ width: '100%', padding: 4 }}
                    >
                      <option value="10">10px</option>
                      <option value="12">12px</option>
                      <option value="14">14px</option>
                      <option value="16">16px</option>
                      <option value="18">18px</option>
                      <option value="20">20px</option>
                      <option value="24">24px</option>
                    </select>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Font Family</Typography>
                    <select
                      value={shapeStyle?.fontFamily || 'Arial'}
                      onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
                      style={{ width: '100%', padding: 4 }}
                    >
                      <option value="Arial">Arial</option>
                      <option value="Helvetica">Helvetica</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Courier New">Courier New</option>
                      <option value="Verdana">Verdana</option>
                    </select>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={shapeStyle?.bold || false}
                        onChange={(e) => handleStyleChange('bold', e.target.checked)}
                        style={{ marginRight: 8 }}
                      />
                      Bold
                    </label>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Rotation (degrees)</Typography>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      step="15"
                      value={shapeStyle?.rotation || 0}
                      onChange={(e) => handleStyleChange('rotation', parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {shapeStyle?.rotation || 0}°
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleStyleChange('rotation', ((shapeStyle?.rotation || 0) - 90 + 360) % 360)} 
                        title="Rotate left 90°"
                      >
                        <RotateLeftIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleStyleChange('rotation', ((shapeStyle?.rotation || 0) + 90) % 360)} 
                        title="Rotate right 90°"
                      >
                        <RotateRightIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Width: {shapeStyle?.width || 200}px
                    </Typography>
                    <input
                      type="range"
                      min="50"
                      max="500"
                      step="10"
                      value={shapeStyle?.width || 200}
                      onChange={(e) => handleStyleChange('width', parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Height: {shapeStyle?.height || 24}px
                    </Typography>
                    <input
                      type="range"
                      min="20"
                      max="200"
                      step="5"
                      value={shapeStyle?.height || 24}
                      onChange={(e) => handleStyleChange('height', parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    Click to place text on the chart. Double-click to edit text with multi-line support. Text is automatically saved when clicking outside the editor, or click ✓ to save, ✕ to cancel. Text will wrap to fit the width.
                  </Typography>
                </>
              )}

              {/* Icon specific properties */}
              {currentShape === 'icon' && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Icon Type</Typography>
                    <select
                      value={shapeStyle?.iconType || 'arrow'}
                      onChange={(e) => handleStyleChange('iconType', e.target.value)}
                      style={{ width: '100%', padding: 4 }}
                    >
                      {iconOptions.filter(opt => opt.value).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    
                    {/* Icon Preview */}
                    <Box sx={{ 
                      mt: 1, 
                      p: 2, 
                      border: '1px solid #ddd', 
                      borderRadius: 1, 
                      display: 'flex', 
                      justifyContent: 'center',
                      bgcolor: 'grey.50'
                    }}>
                      <svg width="32" height="32" viewBox="0 0 10 10">
                        {renderIconSvg(
                          shapeStyle?.iconType || 'arrow', 
                          shapeStyle?.color || '#000000', 
                          shapeStyle?.filled || false
                        )}
                      </svg>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Size: {shapeStyle?.size || 16}px
                    </Typography>
                    <input
                      type="range"
                      min="12"
                      max="60"
                      step="2"
                      value={shapeStyle?.size || 16}
                      onChange={(e) => handleStyleChange('size', parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={shapeStyle?.filled || false}
                        onChange={(e) => handleStyleChange('filled', e.target.checked)}
                        style={{ marginRight: 8 }}
                      />
                      Filled
                    </label>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Rotation (degrees)</Typography>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      step="15"
                      value={shapeStyle?.rotation || 0}
                      onChange={(e) => handleStyleChange('rotation', parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {shapeStyle?.rotation || 0}°
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleStyleChange('rotation', ((shapeStyle?.rotation || 0) - 90 + 360) % 360)} 
                        title="Rotate left 90°"
                      >
                        <RotateLeftIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleStyleChange('rotation', ((shapeStyle?.rotation || 0) + 90) % 360)} 
                        title="Rotate right 90°"
                      >
                        <RotateRightIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    Click to place an icon on the chart. Icons will be drawn on top of stitches.
                  </Typography>
                </>
              )}
            </Box>
          )}
        </>
      )}

      {/* Instructions */}
      <Box sx={{ mt: 'auto', p: 1, bgcolor: 'info.main', color: 'info.contrastText', borderRadius: 1 }}>
        <Typography variant="caption">
          {shapeSelectMode ? 
            (selectedShape ? 'Selected shape can be moved by dragging or resized using the corner/edge handles. Text shapes support width/height adjustment for automatic text wrapping.' : 'Click on shapes to select them.') : 
            'Shapes will be drawn on top of stitches and colors.'
          }
        </Typography>
      </Box>
    </Box>
  );
};

export default ShapesPanel;
